import React from 'react';

import { Box, Button, Divider, Typography } from '@mui/material';

import { DEFAULT_VALUE_IF_NIL } from '@clearverify/constants';

import { VerifierCredentialStatus } from 'src/api';
import { VERIFIED_STATUS_ICON } from 'src/assets';
import { FullscreenLoader, Tooltip } from 'src/components/common';
import { requestBodyContainerCVStyles } from 'src/components/inboxItem/InboxItemContentVerificationRequest/shared/styles';
import { Avatar, InfoBlock } from 'src/components/shared';
import { ModalId } from 'src/constants/modals';
import { VERIFICATION_REQUEST_TYPE_LABEL } from 'src/constants/verificationRequest';
import { useInboxItemParamId } from 'src/hooks/inboxItem';
import { useModal } from 'src/hooks/shared';
import {
  useVerificationRequest,
  useVerificationRequestProcessing,
} from 'src/hooks/verificationRequest';
import { useVerifierByVerificationRequestId } from 'src/hooks/verifier';
import {
  ApproveVerificationRequestByEmployeeModalArgs,
  DeclineVerificationRequestByEmployeeModalArgs,
} from 'src/modals/verificationRequest';
import { SxProp } from 'src/types/mui';

export const EmployeeToRespondContent: React.FC = () => {
  const { openModal } = useModal();

  const inboxItemId = useInboxItemParamId();
  const { verificationRequest, loading: verificationRequestLoading } = useVerificationRequest({
    inboxItemId,
  });
  const { verifier, loading: verifierLoading } = useVerifierByVerificationRequestId(
    verificationRequest?.id,
  );

  const { isVerifierResponding } = useVerificationRequestProcessing();
  const isVerifierResponseInProgress = isVerifierResponding(inboxItemId);

  const handleApprove = React.useCallback(() => {
    if (!inboxItemId) return;
    openModal(ModalId.ApproveVerificationRequestByEmployee, {
      inboxItemId,
    } satisfies ApproveVerificationRequestByEmployeeModalArgs);
  }, [openModal, inboxItemId]);

  const handleDecline = React.useCallback(() => {
    if (!inboxItemId) return;
    openModal(ModalId.DeclineVerificationRequestByEmployee, {
      inboxItemId,
    } satisfies DeclineVerificationRequestByEmployeeModalArgs);
  }, [openModal, inboxItemId]);

  if (isVerifierResponseInProgress) {
    return <FullscreenLoader />;
  }

  return (
    <Box sx={requestBodyContainerCVStyles}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" gap={1}>
          <Avatar sx={avatarStyles} src={verifier?.logo?.downloadUrl} />
          <Box>
            <Typography variant="body1" fontWeight={600} lineHeight={1.3}>
              {verifier?.displayName}
            </Typography>

            <Typography
              variant="body1"
              lineHeight={1.3}
              sx={theme => ({
                color: theme.palette.text.secondary,
                mr: 0.5,
              })}
            >
              Verifier
            </Typography>
          </Box>
        </Box>

        {verifier?.credentialStatus === VerifierCredentialStatus.Credentialed && (
          <Box sx={{ fontSize: 0 }}>
            <Tooltip title={credentialedTooltipText} arrow placement="right">
              <img width={21} height={21} alt="verifiedStatusIcon" src={VERIFIED_STATUS_ICON} />
            </Tooltip>
          </Box>
        )}
      </Box>

      <Divider sx={requestBodyDividerStyles} />

      <InfoBlock.Container mt={2} gap={1.5}>
        <InfoBlock.Item
          title="Report Type"
          value={
            verificationRequest?.type
              ? VERIFICATION_REQUEST_TYPE_LABEL[verificationRequest.type]
              : DEFAULT_VALUE_IF_NIL
          }
          loading={verificationRequestLoading}
        />
        <InfoBlock.Item
          title="Contact Name"
          value={verifier?.contactName}
          loading={verifierLoading}
        />
        <InfoBlock.Item
          title="Contact Email"
          value={verifier?.contactEmail}
          loading={verifierLoading}
        />
        <InfoBlock.Item
          title="Contact Phone"
          value={verifier?.contactPhone}
          loading={verifierLoading}
        />
      </InfoBlock.Container>

      <Box display="flex" justifyContent="flex-end" mt={4} gap={2}>
        <Button
          color="error"
          variant="outlined"
          data-e2e-element="decline-request"
          onClick={handleDecline}
        >
          Decline
        </Button>

        <Button variant="outlined" data-e2e-element="approve-request" onClick={handleApprove}>
          Approve
        </Button>
      </Box>
    </Box>
  );
};

const requestBodyDividerStyles: SxProp = theme => ({
  mt: 2.5,
  borderColor: theme.palette.secondary.main,
});

const avatarStyles: SxProp = { width: 42, height: 42 };

const credentialedTooltipText =
  'This verifier has been thoroughly vetted and certified by Clear Verify, confirming their identity, locations, standing with government agencies, and more.';
